"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AiSpendEventSchema = exports.AiSpendOutcomeSchema = exports.AiSpendBucketSchema = exports.AI_SPEND_BUCKETS = exports.AiActorTypeSchema = exports.AI_ACTOR_TYPES = exports.AiVibeRowSchema = exports.AiActionRowSchema = exports.AiVibeSeasonSchema = exports.AiMonthDaySchema = exports.AI_VIBE_IDS = exports.LEADS_DESK_ACTION_IDS = exports.PLATFORM_ACTOR_TENANT_ID = exports.AiActionBillingSchema = exports.AI_ACTION_BILLING = exports.AiActionIdSchema = exports.AI_ACTION_IDS = exports.AiActionGroupSchema = exports.AI_ACTION_GROUPS = exports.ModelRefSchema = exports.AiImageCapabilitySchema = exports.AI_IMAGE_CAPABILITIES = exports.AiModelUsedForSchema = exports.AI_MODEL_USED_FOR = exports.AiRoleRowSchema = exports.AiModelRowSchema = exports.AiUsageCountsSchema = exports.AiProviderRowSchema = exports.AiUnitPricesSchema = exports.AiRoleIdSchema = exports.AI_ROLE_IDS = void 0;
exports.actionBilling = actionBilling;
exports.isVibeInSeason = isVibeInSeason;
exports.isVibeNew = isVibeNew;
const zod_1 = require("zod");
/**
 * Platform AI registry — product-neutral.
 *
 * Rows live in `{tablePrefix}-dynamodb-ai-registry-{stage}` under pk='AI'.
 * Callers request a logical ROLE and never name a vendor or model, so
 * swapping providers is a single row edit with no deploy.
 *
 * See docs/superpowers/specs/2026-09-01-ai-control-center-design.md §3.
 */
exports.AI_ROLE_IDS = [
    'fast-text', 'deep-text', 'vision', 'grounding', 'image-gen', 'video-gen',
];
exports.AiRoleIdSchema = zod_1.z.enum(exports.AI_ROLE_IDS);
/** Only the units a given model actually bills on are present. */
exports.AiUnitPricesSchema = zod_1.z.object({
    input_per_million_usd: zod_1.z.number().nonnegative().optional(),
    output_per_million_usd: zod_1.z.number().nonnegative().optional(),
    cache_write_per_million_usd: zod_1.z.number().nonnegative().optional(),
    cache_read_per_million_usd: zod_1.z.number().nonnegative().optional(),
    per_image_usd: zod_1.z.number().nonnegative().optional(),
    per_call_usd: zod_1.z.number().nonnegative().optional(),
    per_second_usd: zod_1.z.number().nonnegative().optional(),
}).passthrough();
exports.AiProviderRowSchema = zod_1.z.object({
    pk: zod_1.z.literal('AI'),
    sk: zod_1.z.string().regex(/^PROVIDER#/),
    label: zod_1.z.string().min(1),
    vendor: zod_1.z.string().min(1),
    enabled: zod_1.z.boolean().default(true),
    /** SSM SecureString parameter path. Never the secret itself. */
    credentialsRef: zod_1.z.string().min(1),
    authType: zod_1.z.enum(['header', 'query', 'serviceAccount']).default('header'),
    authHeaderName: zod_1.z.string().optional(),
    authPrefix: zod_1.z.string().optional(),
    endpointTemplate: zod_1.z.string().optional(),
    requestTemplate: zod_1.z.unknown().optional(),
    responseParser: zod_1.z.unknown().optional(),
    createdAt: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string().optional(),
});
/**
 * Raw usage counts for a vendor call — the shape vendor SDKs return before
 * pricing is applied. Also used as a "typical usage" estimate on a MODEL#
 * row for cost preview UIs.
 */
exports.AiUsageCountsSchema = zod_1.z.object({
    inputTokens: zod_1.z.number().nonnegative().optional(),
    outputTokens: zod_1.z.number().nonnegative().optional(),
    cacheWriteTokens: zod_1.z.number().nonnegative().optional(),
    cacheReadTokens: zod_1.z.number().nonnegative().optional(),
    images: zod_1.z.number().nonnegative().optional(),
    calls: zod_1.z.number().nonnegative().optional(),
    seconds: zod_1.z.number().nonnegative().optional(),
});
exports.AiModelRowSchema = zod_1.z.object({
    pk: zod_1.z.literal('AI'),
    sk: zod_1.z.string().regex(/^MODEL#/),
    label: zod_1.z.string().min(1),
    enabled: zod_1.z.boolean().default(true),
    capabilities: zod_1.z.array(zod_1.z.string()).default([]),
    unitPrices: exports.AiUnitPricesSchema,
    lastSyncedAt: zod_1.z.string().optional(),
    lastVerifiedAt: zod_1.z.string().optional(),
    /** Vendor model version/snapshot id, e.g. "2025-08". Informational. */
    version: zod_1.z.string().optional(),
    /** Typical usage counts for this model, for cost-preview UIs. */
    typicalUsage: exports.AiUsageCountsSchema.optional(),
});
exports.AiRoleRowSchema = zod_1.z.object({
    pk: zod_1.z.literal('AI'),
    sk: zod_1.z.string().regex(/^ROLE#/),
    label: zod_1.z.string().min(1),
    enabled: zod_1.z.boolean().default(true),
    providerId: zod_1.z.string().min(1),
    modelId: zod_1.z.string().min(1),
    fallbackProviderId: zod_1.z.string().optional(),
    fallbackModelId: zod_1.z.string().optional(),
    /** Tenant-facing knobs. A vendor swap must never change these. */
    burnRate: zod_1.z.number().nonnegative().max(50),
    minCreditsPerCall: zod_1.z.number().int().nonnegative().default(1),
    reserveCap: zod_1.z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// Ruby AI Actions & Vibes (spec 2026-09-27-ruby-ai-actions-design.md §2).
// ADDITIVE ONLY — ROLE# rows stay and remain the fallback for any caller not
// yet converted to an action.
// ---------------------------------------------------------------------------
/**
 * "Used for" vocabulary for MODEL# rows. Stored in the existing
 * `AiModelRow.capabilities: string[]` field (which stays a free string array —
 * narrowing it would be a breaking change); this list is what UIs offer and
 * what action editors filter default/fallback models by.
 */
exports.AI_MODEL_USED_FOR = [
    'text', 'web_search', 'image', 'image_edit', 'video', 'voice', 'long_form',
    // --- 2026-10-04, Ruby Generation Engine — per-step image capabilities ---
    'draws_images', 'edits_images', 'finds_images', 'reads_images',
    // --- 2026-10-15, Ruby Estate insights — data steps ---
    'property_data', 'area_data', 'maps',
];
exports.AiModelUsedForSchema = zod_1.z.enum(exports.AI_MODEL_USED_FOR);
/** The subset of AI_MODEL_USED_FOR used to tag per-step image capabilities. */
exports.AI_IMAGE_CAPABILITIES = ['draws_images', 'edits_images', 'finds_images', 'reads_images'];
exports.AiImageCapabilitySchema = zod_1.z.enum(exports.AI_IMAGE_CAPABILITIES);
/** A provider/model pair — used for step defaults, fallbacks and vibe overrides. */
exports.ModelRefSchema = zod_1.z.object({
    providerId: zod_1.z.string().min(1),
    modelId: zod_1.z.string().min(1),
});
/** Action groups, in display order. */
exports.AI_ACTION_GROUPS = ['text', 'image', 'video', 'voice'];
exports.AiActionGroupSchema = zod_1.z.enum(exports.AI_ACTION_GROUPS);
/**
 * Seeded action ids (D2). Only `text.product_description` is live; the rest
 * are seeded DISABLED. The row's `sk` is `ACTION#<actionId>`.
 */
exports.AI_ACTION_IDS = [
    'text.product_description',
    'text.legal_agreement',
    'image.single_object',
    'image.multiple_objects',
    'video.clip',
    'voice.clip',
    // --- 2026-10-08, careers (Ruby Token economy) ---
    'text.job_description',
    'image.job_ad',
    // --- 2026-10-11, Ruby Estate property hub (spec 2026-10-11 §8) ---
    'data.property_lookup',
    'data.property_valuation',
    'data.rent_estimate',
    'text.property_analysis',
    // --- 2026-10-15, Ruby Estate property insights (spec 2026-10-15-estate-property-insights) ---
    'data.area_insights',
    // --- 2026-10-14, Ruby Estate lease workspace (spec 2026-10-14 §5.3) ---
    'text.lease_law_review',
    'text.lease_draft',
    // --- 2026-10-15, Ruby Estate lease review-with-Ruby (spec 2026-10-15) ---
    'text.lease_review',
    // --- 2026-10-15, Ruby Estate master lease (spec 2026-10-15-estate-lease-esign-master §7) ---
    'text.lease_templatize',
    // --- 2026-10-11, Product Intelligence → Product Alerts (restock forecast) ---
    'text.product_intelligence',
    // --- 2026-10-11, Leads Desk — internal · not billed (spec 2026-10-11-leads-desk §4.3) ---
    'text.lead_review',
    'text.sales_email_draft',
    'text.sales_coach',
    'text.lead_summary',
    // --- 2026-10-13, Ruby Social Publishing (spec 2026-10-13) ---
    'image.social_post',
    'text.social_caption',
    'social.publish.google_business',
    'social.publish.facebook',
    'social.publish.instagram',
    // --- 2026-10-15, Ruby Identity verification (spec 2026-10-15-identity-verification-design) ---
    'identity.document_check', 'identity.ssn_check', 'identity.export', 'identity.import', 'identity.storage', 'identity.age_check',
    // --- 2026-10-17, Document Management storage meter (spec 2026-10-17-document-management-design, phase 1B) ---
    'storage.documents', 'storage.product_images', 'storage.requests', 'storage.egress',
    // --- 2026-10-17, Document Management — Ruby reads the invoice (vision) ---
    'documents.read_invoice',
];
exports.AiActionIdSchema = zod_1.z.enum(exports.AI_ACTION_IDS);
/**
 * Who pays for an action (Leads Desk spec §4.3). 'tenant' = the existing Ruby
 * Token economy. 'internal' = a platform cost: the run is metered (SPEND row +
 * RUN# line with its real vendor cost) but nobody is charged, and the router
 * REFUSES it for any tenant id other than PLATFORM_ACTOR_TENANT_ID — an
 * internal action can never become a free path for tenants.
 */
exports.AI_ACTION_BILLING = ['tenant', 'internal'];
exports.AiActionBillingSchema = zod_1.z.enum(exports.AI_ACTION_BILLING);
/** Absent = 'tenant' (every row seeded before 2026-10-11). */
function actionBilling(row) {
    return row?.billing === 'internal' ? 'internal' : 'tenant';
}
/** The tenantId every internal action runs (and is ledgered) as. */
exports.PLATFORM_ACTOR_TENANT_ID = 'rubytill-platform';
exports.LEADS_DESK_ACTION_IDS = [
    'text.lead_review', 'text.sales_email_draft', 'text.sales_coach', 'text.lead_summary',
];
/** Seeded vibe ids (D5). The row's `sk` is `VIBE#<vibeId>`. */
exports.AI_VIBE_IDS = ['professional', 'summer', 'christmas', 'valentine', 'halloween'];
/** Month-day "MM-DD", e.g. "11-15". */
exports.AiMonthDaySchema = zod_1.z.string().regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/);
/**
 * A seasonal window. `from` may be later than `to` — the window then wraps
 * the new year (e.g. 12-20 → 01-05). Both ends inclusive.
 */
exports.AiVibeSeasonSchema = zod_1.z.object({
    fromMonthDay: exports.AiMonthDaySchema,
    toMonthDay: exports.AiMonthDaySchema,
});
exports.AiActionRowSchema = zod_1.z.object({
    pk: zod_1.z.literal('AI'),
    sk: zod_1.z.string().regex(/^ACTION#/),
    label: zod_1.z.string().min(1),
    group: exports.AiActionGroupSchema,
    enabled: zod_1.z.boolean().default(true),
    description: zod_1.z.string().optional(),
    providerId: zod_1.z.string().min(1),
    modelId: zod_1.z.string().min(1),
    fallbackProviderId: zod_1.z.string().optional(),
    fallbackModelId: zod_1.z.string().optional(),
    /** Base multiplier on vendor cost. A vibe's multiplier REPLACES it (D4). */
    multiplier: zod_1.z.number().nonnegative().max(50),
    /** Minimum charge per run, in Ruby Tokens (display unit, D7). */
    minTokens: zod_1.z.number().int().nonnegative(),
    /** Maximum charge per run, in Ruby Tokens (display unit, D7). Also the reserve. */
    maxTokens: zod_1.z.number().int().positive(),
    /** Package feature that gates the action. */
    featureId: zod_1.z.string().min(1),
    /** Where the action is triggered — informational ("where it runs" panel). */
    trigger: zod_1.z.object({ surface: zod_1.z.string(), file: zod_1.z.string().optional() }).optional(),
    /** Where the action executes — informational. */
    runs: zod_1.z.object({ service: zod_1.z.string(), endpoint: zod_1.z.string().optional() }).optional(),
    createdAt: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string().optional(),
    // --- 2026-10-04, Ruby Generation Engine (all optional, additive) ---
    /** Multi-step pipeline (e.g. draw → edit). Absent = single-step, using the
     * top-level providerId/modelId above. */
    steps: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.string().min(1),
        capability: zod_1.z.string().min(1),
        providerId: zod_1.z.string().min(1),
        modelId: zod_1.z.string().min(1),
        fallbacks: zod_1.z.array(exports.ModelRefSchema).default([]),
    })).optional(),
    /** Target entity types this action can run against. */
    targetTypes: zod_1.z.array(zod_1.z.string().min(1)).optional(),
    /** Prompt template per target type; `default` covers any type without its own key. */
    promptTemplates: zod_1.z.record(zod_1.z.string()).optional(),
    timeoutSec: zod_1.z.number().int().positive().max(3600).optional(),
    // --- 2026-10-11, Leads Desk (optional, additive; absent = 'tenant') ---
    billing: exports.AiActionBillingSchema.optional(),
});
exports.AiVibeRowSchema = zod_1.z.object({
    pk: zod_1.z.literal('AI'),
    sk: zod_1.z.string().regex(/^VIBE#/),
    label: zod_1.z.string().min(1),
    /** Swatch colour, e.g. "#DC143C". */
    color: zod_1.z.string().optional(),
    enabled: zod_1.z.boolean().default(true),
    /** Actions this vibe may be used with. */
    actionIds: zod_1.z.array(zod_1.z.string().min(1)).default([]),
    /** Actions for which this vibe is the default (one default per action). */
    defaultFor: zod_1.z.array(zod_1.z.string().min(1)).default([]),
    background: zod_1.z.string().default(''),
    context: zod_1.z.string().default(''),
    promptCustomisation: zod_1.z.string().default(''),
    /** Model override; absent = the action's model. */
    providerId: zod_1.z.string().optional(),
    modelId: zod_1.z.string().optional(),
    /** REPLACES the action's base multiplier when set; absent = action's. */
    multiplier: zod_1.z.number().nonnegative().max(50).optional(),
    /** Seasonal window; absent = always available. */
    season: exports.AiVibeSeasonSchema.optional(),
    /** Package feature that unlocks the vibe; absent = available to every tier that has the action. */
    featureId: zod_1.z.string().optional(),
    /** Show a NEW badge for this many days after `createdAt` (or `announcedAt`). */
    announceNewDays: zod_1.z.number().int().nonnegative().optional(),
    /** When "Announce as NEW" was pressed; falls back to createdAt. */
    announcedAt: zod_1.z.string().optional(),
    sortOrder: zod_1.z.number().optional(),
    createdAt: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string().optional(),
    // --- 2026-10-04, Ruby Generation Engine (all optional, additive) ---
    /** Per-action model override, keyed by actionId. Absent = the action/step default. */
    modelOverrides: zod_1.z.record(exports.ModelRefSchema).optional(),
    /** When true, this vibe edits the existing scene rather than regenerating it. */
    keepScene: zod_1.z.boolean().optional(),
});
/**
 * True when `date` falls inside `season` (inclusive, UTC month/day). No season
 * = always in season. Handles windows that wrap the new year.
 */
function isVibeInSeason(season, date = new Date()) {
    if (!season)
        return true;
    const md = `${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
    const { fromMonthDay: from, toMonthDay: to } = season;
    return from <= to ? md >= from && md <= to : md >= from || md <= to;
}
/**
 * True while a vibe should carry its NEW badge: within `announceNewDays` of
 * `announcedAt ?? createdAt`. Missing fields read as "not new".
 */
function isVibeNew(vibe, now = new Date()) {
    const days = vibe.announceNewDays;
    const from = vibe.announcedAt ?? vibe.createdAt;
    if (!days || !from)
        return false;
    const t = Date.parse(from);
    if (Number.isNaN(t))
        return false;
    return now.getTime() - t < days * 86400000 && now.getTime() >= t;
}
/** Who caused a spend event. `admin_test` runs are never charged (D9). */
exports.AI_ACTOR_TYPES = ['tenant', 'admin_test', 'platform'];
exports.AiActorTypeSchema = zod_1.z.enum(exports.AI_ACTOR_TYPES);
/**
 * Which wallet bucket paid. `mixed` = the charge straddled allowance and
 * purchased (split in `allowanceCredits` / `purchasedCredits`); `none` = not
 * charged (admin test, platform, free tenant, failed call). `overage` =
 * additional Ruby Token usage (opt-in, capped) — added 2026-10-11, additive.
 */
exports.AI_SPEND_BUCKETS = ['allowance', 'purchased', 'mixed', 'none', 'overage'];
exports.AiSpendBucketSchema = zod_1.z.enum(exports.AI_SPEND_BUCKETS);
exports.AiSpendOutcomeSchema = zod_1.z.enum(['success', 'failed', 'aborted']);
/**
 * Append-only. Corrections are compensating rows, never updates or
 * deletes — this is the company AI expense record (spec §9.3).
 */
exports.AiSpendEventSchema = zod_1.z.object({
    pk: zod_1.z.string().regex(/^TENANT#/),
    sk: zod_1.z.string().regex(/^SPEND#/),
    tenantId: zod_1.z.string().min(1),
    service: zod_1.z.string().min(1),
    feature: zod_1.z.string().min(1),
    roleId: zod_1.z.string().min(1),
    providerId: zod_1.z.string().min(1),
    modelId: zod_1.z.string().min(1),
    inputTokens: zod_1.z.number().int().nonnegative().default(0),
    outputTokens: zod_1.z.number().int().nonnegative().default(0),
    cacheWriteTokens: zod_1.z.number().int().nonnegative().default(0),
    cacheReadTokens: zod_1.z.number().int().nonnegative().default(0),
    units: zod_1.z.number().nonnegative().default(0),
    attempts: zod_1.z.number().int().positive().default(1),
    costUsd: zod_1.z.number().nonnegative(),
    costEstimated: zod_1.z.boolean().default(false),
    creditsCharged: zod_1.z.number().nonnegative().default(0),
    latencyMs: zod_1.z.number().int().nonnegative().default(0),
    outcome: exports.AiSpendOutcomeSchema,
    reservationId: zod_1.z.string().optional(),
    errorMessage: zod_1.z.string().optional(),
    occurredAt: zod_1.z.string().min(1),
    // --- 2026-09-27, Ruby AI Actions (all optional, additive) ---
    actionId: zod_1.z.string().optional(),
    vibeId: zod_1.z.string().optional(),
    actorType: exports.AiActorTypeSchema.optional(),
    /** Cognito sub / email of the admin for `admin_test`; tenant user otherwise. */
    actorId: zod_1.z.string().optional(),
    bucket: exports.AiSpendBucketSchema.optional(),
    allowanceCredits: zod_1.z.number().nonnegative().optional(),
    purchasedCredits: zod_1.z.number().nonnegative().optional(),
    /**
     * One generation run can bill several vendor calls (grounding + a text
     * fallback). Every event of the run carries the same runId, so reports
     * count runs, not calls. Absent on older rows = one event, one run.
     */
    runId: zod_1.z.string().optional(),
    // --- 2026-10-04, Ruby Generation Engine (all optional, additive) ---
    /** Which pipeline step within the run this event bills (e.g. "draw"). */
    step: zod_1.z.string().optional(),
    /** Vendor model version/snapshot id actually used. */
    modelVersion: zod_1.z.string().optional(),
});
//# sourceMappingURL=ai-registry.schema.js.map